import React, { useEffect, useMemo, useState } from 'react';

import {
  Flex, Stack, Text, useToast,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery } from 'react-query';
import { useParams, useHistory } from 'react-router-dom';

import { createRole, getRole, updateRole } from 'api/roles';
import { Icon, Button } from 'components';
import { breadcrumbs } from 'constants/routes';
import BreadcrumbMenu from 'containers/Breadcrumb';
import Page from 'containers/Page';
import RoleTable from 'containers/Roles/Table';
import { defaultPermissions } from 'containers/Roles/constants';
import { AccessTypes, MethodTypes, RoleTypes } from 'enums/types';
import { policy } from 'polices';

function parsePermissions(permissions) {
  return Object.entries(permissions).map(([id, value]:[string, any]) => ({
    ...defaultPermissions.find(({ key }) => key === id),
    ...value,
  }));
}

type Role = {
  name?: string
  type: RoleTypes
  permissions: typeof defaultPermissions
}

export default function Roles() {
  const { id } = useParams<GeneralRoute>();
  const { push } = useHistory();
  const toast = useToast();
  const policies = policy('roles');
  const [isEditing, setIsEditing] = useState(!id);
  const [role, setRole] = useState<Role>({
    name: null,
    type: RoleTypes.Custom,
    permissions: defaultPermissions,
  });

  const isEditable = useMemo(() => policies(MethodTypes.Update) === AccessTypes.Allow, [policies]);

  useEffect(() => {
    if (policies(MethodTypes.View) !== AccessTypes.Allow) {
      push('/403');
    }
  }, []);

  const form = useForm();

  const { setValue, setError } = form;

  useQuery(['role', id], () => getRole({ id }), {
    enabled: !!id,
    onSuccess: ({ data }) => {
      setRole({
        ...data,
        permissions: parsePermissions(data.permissions),
      });
      setValue('name', data.name);
    },
  });

  const { mutate } = useMutation((data) => {
    if (id) {
      return updateRole({ id, data });
    }
    return createRole({ data });
  }, {
    onSuccess: () => {
      toast({
        title: 'Salvo com sucesso!',
        description: 'Permissão salva com sucesso!',
        status: 'success',
        duration: 5000,
        position: 'top-right',
        isClosable: true,
      });
      setIsEditing(false);
      push('/table/roles');
    },
    onError: (error:any) => {
      if (error.errors) {
        error.errors.forEach((err) => setError(err.field, err));
      } else {
        toast({
          title: 'Ocorreu um erro.',
          description: error.message,
          status: 'error',
          duration: 5000,
          position: 'top-right',
          isClosable: true,
        });
      }
    },
  });

  const onSubmit = (data) => mutate(data);

  return (
    <Page>
      <Stack
        w="1024px"
        alignSelf="center">
        <BreadcrumbMenu paths={[
          ...breadcrumbs.roles,
          {
            path: window.location.pathname,
            label: role.name || 'Nova',
          },
        ]}
        />
        <Flex
          width="100%"
          justifyContent="space-between">
          <Text variant="3xLargeBold">{role.name}</Text>
          <Button
            hidden={!isEditable || isEditing || role.type === RoleTypes.Default}
            onClick={() => setIsEditing(true)}
            bg="white.default"
            borderRadius={50}
            h={8}
            _active={{
              color: 'white.default',
            }}
            _hover={{
              bg: 'primary.700',
              color: 'white.default',
            }}
            color="gray.500">
            <Text variant="xSmallSemiBold">Editar</Text>
            <Icon
              ml={2}
              name="edit"
            />
          </Button>
        </Flex>
        <RoleTable
          form={form}
          role={role}
          onSubmit={onSubmit}
          editable={isEditing}
          setEditable={setIsEditing}
        />
      </Stack>
    </Page>
  );
}
